import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, contentChild, input, output } from '@angular/core';
import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';

import { ProgressBarComponent } from '../progress-bar';
import { StatusMatrixCellDirective } from './status-matrix-cell.directive';
import { StatusMatrixCell, StatusMatrixCellEvent, StatusMatrixRow } from './status-matrix.model';

/**
 * Generic "rows × time slots" status grid, e.g. import/export monitors.
 * Each row has a label and a list of cells; cells are rendered as `dmb-progress-bar` unless a
 * `ng-template[dmbStatusMatrixCell]` is projected.
 *
 * Cells can carry additional data (`C extends StatusMatrixCell`), which is passed back with `cellClick`.
 */
@Component({
  selector: 'dmb-status-matrix',
  templateUrl: './status-matrix.component.html',
  styleUrl: './status-matrix.component.scss',
  imports: [MatTooltip, NgTemplateOutlet, ProgressBarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusMatrixComponent<C extends StatusMatrixCell = StatusMatrixCell> {
  readonly rows = input.required<readonly StatusMatrixRow<C>[]>();
  /** Header of the label column, e.g. "Store name" */
  readonly labelHeader = input('');
  /** Optional column headers (e.g. dates). Missing headers are rendered empty. */
  readonly columnHeaders = input<readonly string[]>([]);
  readonly showFooter = input(true);
  readonly tooltipPosition = input<TooltipPosition>('below');
  /** Renders cells as buttons and emits `cellClick` */
  readonly cellsClickable = input(false);

  readonly cellClick = output<StatusMatrixCellEvent<C>>();

  readonly cellTemplate = contentChild(StatusMatrixCellDirective);

  readonly columnCount = computed(() =>
    Math.max(this.columnHeaders().length, ...this.rows().map((row) => row.cells.length), 0),
  );

  readonly headers = computed(() =>
    Array.from({ length: this.columnCount() }, (_, i) => this.columnHeaders()[i] ?? ''),
  );

  /** Number of row items, shown in the footer */
  readonly rowCount = computed(() => this.rows().length);

  onCellClick(row: StatusMatrixRow<C>, cell: C, index: number) {
    if (this.cellsClickable()) {
      this.cellClick.emit({ row, cell, index });
    }
  }
}
